<script setup>

import {computed, onMounted, reactive, ref, watch} from "vue";
import {useAdminStore} from "@/store/admin";
import {useSettingsStore} from "@/store/settings";
import {useToast} from "vue-toast-notification";
import MainPreloader from "@/components/shared/MainPreloader.vue";
import EmptyCard from "@/components/shared/EmptyCard.vue";
import FormInput from "@/components/ui/FormInput.vue";
import CheckBox from "@/components/ui/CheckBox.vue";
import MultiSelect from "@/components/ui/MultiSelect.vue";
import ActionButton from "@/components/ui/ActionButton.vue";
import TablePager from "@/components/shared/TablePager.vue";
import AdminModal from "@/components/AdminView/AdminModal.vue";
import FileButton from "@/components/ui/FileButton.vue";
import {openImagePreview} from "@/composables/image-preview";

const adminStore = useAdminStore();
const settingsStore = useSettingsStore();
const toast = useToast();

const canManageRoles = computed(() => settingsStore.hasAnyRole(['admin', 'superadmin']));
const isSuperadmin = computed(() => settingsStore.hasRole('superadmin'));

// Роли, доступные для назначения: admin не может назначать superadmin
const assignableRoles = computed(() => {
    const roles = adminStore.roles || [];
    if (isSuperadmin.value) return roles;
    return roles.filter(role => role.name !== 'superadmin');
});

const roleOptions = computed(() => assignableRoles.value.map(role => ({
    value: role.name,
    label: role.category ? `${role.name} (${role.category})` : role.name,
})));

// Admin не может редактировать и удалять пользователей с ролью superadmin
function canMutateUser(item) {
    if (isSuperadmin.value) return true;
    return !(item?.roles || []).includes('superadmin');
}

function canEditUser(item) {
    return canMutateUser(item);
}

function canDeleteUser(item) {
    return canMutateUser(item);
}

const PER_PAGE_STORAGE_KEY = 'adminUsersPerPage';
const currentPage = ref(0);

const PER_PAGE = ref(parseInt(localStorage.getItem(PER_PAGE_STORAGE_KEY)) || 50);

const items = computed(() => adminStore.items);

const FILTERS_STORAGE_KEY = 'adminUsersFilters';
const savedFilters = (() => {
    try { return JSON.parse(localStorage.getItem(FILTERS_STORAGE_KEY)) || {}; } catch { return {}; }
})();
const filter = reactive({
    username: savedFilters.username || "",
    email: savedFilters.email || "",
    roles: Array.isArray(savedFilters.roles) ? savedFilters.roles : [],
});
watch(filter, () => {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({
        username: filter.username,
        email: filter.email,
        roles: filter.roles,
    }));
}, {deep: true});

const countPages = computed(() => Math.max(1, Math.ceil((adminStore.total || 0) / PER_PAGE.value)));
const hasMoreData = computed(() => (adminStore.total || 0) > (currentPage.value + 1) * PER_PAGE.value);

function buildParams() {
    const params = {page: currentPage.value + 1, per_page: PER_PAGE.value};
    if (filter.username) params.username = filter.username;
    if (filter.email) params.email = filter.email;
    if (filter.roles.length) params.roles = filter.roles.join(",");
    return params;
}

async function loadUsers() {
    await adminStore.loadData(buildParams());
}

async function setPage(p) {
    if (p < 0 || p >= countPages.value) return;
    currentPage.value = p;
    await loadUsers();
}

function setPerPage(n) {
    const val = parseInt(n);
    if (!val || val < 1) return;
    PER_PAGE.value = val;
    localStorage.setItem(PER_PAGE_STORAGE_KEY, String(val));
    currentPage.value = 0;
    loadUsers();
}

const pagination = {
    loading: computed(() => adminStore.loading),
    currentPage,
    countPages,
    hasMoreData,
    perPage: PER_PAGE,
    setPage,
    setPerPage,
    firstPage: () => setPage(0),
    decPage: () => setPage(currentPage.value - 1),
    incPage: () => setPage(currentPage.value + 1),
    lastPage: () => setPage(countPages.value - 1),
};

function applyFilters() {
    currentPage.value = 0;
    loadUsers();
}

function resetFilters() {
    filter.username = "";
    filter.email = "";
    filter.roles = [];
    applyFilters();
}

function updateRoleFilter(roles) {
    filter.roles = roles;
    applyFilters();
}

const editing = ref(null);
const form = reactive(defaultForm());
const selectedImage = ref(null);
const imagePreview = ref("");

function selectImage(file) {
    selectedImage.value = file || null;
    if (imagePreview.value) URL.revokeObjectURL(imagePreview.value);
    imagePreview.value = selectedImage.value ? URL.createObjectURL(selectedImage.value) : "";
}

function clearSelectedImage() {
    selectedImage.value = null;
    if (imagePreview.value) URL.revokeObjectURL(imagePreview.value);
    imagePreview.value = "";
}

function defaultForm() {
    return {
        user_name: "",
        first_name: "",
        last_name: "",
        middle_name: "",
        phone: "",
        email: "",
        position: "",
        roles: [],
        info: "",
        city: "",
        is_active: false,
        is_at_club: false,
        email_subscribed: false,
        created_at: "",
        updated_at: "",
        attributes: "",
    };
}

function resetForm() {
    Object.assign(form, defaultForm());
    clearSelectedImage();
}

const passwordForm = reactive({password: "", confirm: ""});
const showPassword = ref(false);

function resetPasswordForm() {
    passwordForm.password = "";
    passwordForm.confirm = "";
}

function formatDate(dateStr) {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleString("ru-RU");
}

function editItem(item) {
    if (!canEditUser(item)) {
        toast.error("Недостаточно прав для редактирования этого пользователя");
        return;
    }
    editing.value = item;
    clearSelectedImage();
    Object.assign(form, {
        user_name: item.user_name || "",
        first_name: item.first_name || "",
        last_name: item.last_name || "",
        middle_name: item.middle_name || "",
        phone: item.phone || "",
        email: item.email || "",
        position: item.position || "",
        roles: [...(item.roles || [])],
        info: item.info || "",
        city: item.city || "",
        is_active: !!item.is_active,
        is_at_club: !!item.is_at_club,
        email_subscribed: !!item.email_subscribed,
        created_at: item.created_at || "",
        updated_at: item.updated_at || "",
        attributes: item.attributes || "",
    });
}

function cancelEdit() {
    editing.value = null;
    resetForm();
    resetPasswordForm();
}

function rolesChanged() {
    const initialRoles = [...(editing.value?.roles || [])].sort();
    const newRoles = [...form.roles].sort();
    return initialRoles.length !== newRoles.length
        || initialRoles.some((role, index) => role !== newRoles[index]);
}

async function saveRoles() {
    if (!canManageRoles.value || !editing.value) return true;
    if (!form.roles.length) {
        toast.error("У пользователя должна быть хотя бы одна роль");
        return false;
    }
    if (!rolesChanged()) return true;
    const data = await adminStore.updateUserRoles(editing.value.id, form.roles);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return false;
    }
    if (data) {
        editing.value.roles = [...(data.roles || [])];
    }
    return true;
}

async function saveHandler() {
    if (!editing.value) return;
    const ok = await adminStore.updateItem('users', editing.value.id, {
        user_name: form.user_name,
        first_name: form.first_name,
        last_name: form.last_name,
        middle_name: form.middle_name,
        phone: form.phone,
        email: form.email,
        position: form.position,
        info: form.info,
        city: form.city,
        is_active: form.is_active,
        is_at_club: form.is_at_club,
        email_subscribed: form.email_subscribed,
        attributes: form.attributes,
    });
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        if (!await saveRoles()) {
            return;
        }
        if (selectedImage.value && editing.value?.id) {
            await adminStore.uploadUserImage(editing.value.id, selectedImage.value);
            if (adminStore.error) {
                toast.error(adminStore.error);
                return;
            }
        }
        toast.success("Пользователь обновлён");
        cancelEdit();
        await loadUsers();
    }
}

async function changePasswordHandler() {
    if (!editing.value) return;
    if (!passwordForm.password || !passwordForm.confirm) {
        toast.error("Заполните оба поля пароля");
        return;
    }
    if (passwordForm.password !== passwordForm.confirm) {
        toast.error("Пароли не совпадают");
        return;
    }
    const ok = await adminStore.resetPassword(editing.value.id, passwordForm.password);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        toast.success("Пароль изменён");
        resetPasswordForm();
    }
}

async function deleteHandler(item) {
    if (!canDeleteUser(item)) {
        toast.error("Недостаточно прав для удаления этого пользователя");
        return;
    }
    if (!confirm(`Удалить пользователя ${item.user_name || item.email || item.id}?`)) return;
    const ok = await adminStore.deleteItem('users', item.id);
    if (adminStore.error) {
        toast.error(adminStore.error);
        return;
    }
    if (ok) {
        toast.success("Пользователь удалён");
        await loadUsers();
    }
}

function fillData() {
    loadUsers();
}

onMounted(() => {
    adminStore.loadRoles();
    fillData();
});

</script>

<template>
  <div class="adminCrud">
    <MainPreloader
        v-if="adminStore.loading"
        position="fixed"
        :size="80"
        :stroke="3"
    />

    <div class="adminCrud__toolbar">
      <div class="adminCrud__toolbarTitle">Пользователи</div>
    </div>

    <AdminModal :visible="!!editing" title="Редактирование пользователя" @close="cancelEdit">
      <div class="adminCrud__formGrid">
        <div class="adminCrud__field">
          <label class="adminCrud__label">Логин</label>
          <form-input v-model="form.user_name"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Email</label>
          <form-input v-model="form.email"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Имя</label>
          <form-input v-model="form.first_name" :name-only="true"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Фамилия</label>
          <form-input v-model="form.last_name" :name-only="true"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Отчество</label>
          <form-input v-model="form.middle_name" :name-only="true"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Телефон</label>
          <form-input v-model="form.phone"/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Должность (Position)</label>
          <form-input v-model="form.position"/>
        </div>
        <div v-if="canManageRoles" class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Роли</label>
          <multi-select
              v-model="form.roles"
              :options="roleOptions"
              placeholder="Роли не выбраны"
          />
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Город</label>
          <form-input v-model="form.city"/>
        </div>
        <div class="adminCrud__field adminCrud__field--wide adminCrud__field--content">
          <label class="adminCrud__label">Информация</label>
          <form-input v-model="form.info" rows="7"/>
        </div>
        <div class="adminCrud__field adminCrud__field--wide">
          <label class="adminCrud__label">Аватар (JPEG, PNG или GIF)</label>
          <FileButton accept="image/jpeg,image/png,image/gif" @select="selectImage"/>
          <img
              v-if="editing?.img_url && !selectedImage"
              :src="editing.img_url"
              alt="Текущий аватар"
              class="adminCrud__imagePreview"
              @click="openImagePreview(editing.img_url)"
          />
          <img
              v-if="imagePreview"
              :src="imagePreview"
              alt="Предпросмотр аватара"
              class="adminCrud__imagePreview"
              @click="openImagePreview(imagePreview)"
          />
        </div>
      </div>
      <div class="adminCrud__infoBlock">
        <div class="adminCrud__field">
          <label class="adminCrud__label">Создан</label>
          <form-input :model-value="formatDate(form.created_at)" disabled/>
        </div>
        <div class="adminCrud__field">
          <label class="adminCrud__label">Обновлён</label>
          <form-input :model-value="formatDate(form.updated_at)" disabled/>
        </div>
      </div>
      <div class="adminCrud__field adminCrud__field--wide adminCrud__field--content">
        <label class="adminCrud__label">Attributes (JSON)</label>
        <form-input v-model="form.attributes" rows="2"/>
      </div>
      <div class="adminCrud__checks">
        <check-box v-model="form.is_active" text="Активен"/>
        <check-box v-model="form.is_at_club" text="В клубе"/>
        <check-box v-model="form.email_subscribed" text="Получать новости и специальные предложения по email"/>
      </div>
      <div class="adminCrud__footer">
        <ActionButton @buttonClick="cancelEdit">
          <template #caption>Отмена</template>
        </ActionButton>
        <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="saveHandler">
          <template #caption>Сохранить</template>
        </ActionButton>
      </div>
      <div class="adminCrud__passwordBlock">
        <div class="adminCrud__formTitle">Смена пароля</div>
        <div class="adminCrud__checks">
          <check-box v-model="showPassword" text="Показать пароль"/>
        </div>
        <div class="adminCrud__formGrid">
          <div class="adminCrud__field">
            <label class="adminCrud__label">Новый пароль</label>
            <form-input v-model="passwordForm.password" :type="showPassword ? 'text' : 'password'"/>
          </div>
          <div class="adminCrud__field">
            <label class="adminCrud__label">Подтверждение пароля</label>
            <form-input v-model="passwordForm.confirm" :type="showPassword ? 'text' : 'password'"/>
          </div>
        </div>
        <div class="adminCrud__formActions">
          <ActionButton style="background-color: #6FC062; color: #212226;" @buttonClick="changePasswordHandler">
            <template #caption>Сменить пароль</template>
          </ActionButton>
        </div>
      </div>
    </AdminModal>

    <div class="adminCrud__filters">
      <div class="adminCrud__filterRow">
        <div class="adminCrud__filterField">
          <label class="adminCrud__label">Логин</label>
          <form-input v-model="filter.username" @input="applyFilters"/>
        </div>
        <div class="adminCrud__filterField">
          <label class="adminCrud__label">Email</label>
          <form-input v-model="filter.email" @input="applyFilters"/>
        </div>
        <div class="adminCrud__filterField">
          <label class="adminCrud__label">Роли</label>
          <multi-select
              :model-value="filter.roles"
              :options="roleOptions"
              placeholder="Роли не выбраны"
              @update:model-value="updateRoleFilter"
          />
        </div>
        <div class="adminCrud__filterActions">
          <ActionButton @buttonClick="resetFilters">
            <template #caption>Сбросить</template>
          </ActionButton>
        </div>
      </div>
    </div>

    <EmptyCard v-if="!items.length && !adminStore.loading"/>

    <TablePager
        v-if="items.length && pagination.countPages.value >= 1"
        :pagination="pagination"
    />

    <table v-if="items.length" class="adminTable">
      <thead>
      <tr>
        <th></th>
        <th>Логин</th>
        <th>Действия</th>
        <th>Email</th>
        <th>Роли</th>
        <th>Активен</th>
        <th>Город</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="item in items" :key="item.id">
        <td>
          <img
              v-if="item.img_url"
              :src="item.img_url"
              alt=""
              class="adminCrud__thumb"
              @click="openImagePreview(item.img_url)"
          />
          <span v-else class="adminCrud__thumb adminCrud__thumb--empty"></span>
        </td>
        <td>{{ item.user_name || '—' }}</td>
        <td class="adminCrud__actions">
          <button
              class="adminCrud__actionButton"
              :disabled="!canEditUser(item)"
              :title="canEditUser(item) ? 'Изменить' : 'Недостаточно прав для редактирования'"
              @click="editItem(item)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button
              class="adminCrud__actionButton adminCrud__actionButton--danger"
              :disabled="!canDeleteUser(item)"
              :title="canDeleteUser(item) ? 'Удалить' : 'Недостаточно прав для удаления'"
              @click="deleteHandler(item)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6h18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </td>
        <td>{{ item.email || '—' }}</td>
        <td>{{ (item.roles || []).join(', ') || '—' }}</td>
        <td>{{ item.is_active ? 'Да' : 'Нет' }}</td>
        <td>{{ item.city || '—' }}</td>
      </tr>
      </tbody>
    </table>

    <TablePager
        v-if="pagination.countPages.value >= 1"
        :pagination="pagination"
    />
  </div>
</template>

<style scoped lang="scss">
.adminCrud {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
  }

  &__toolbarTitle {
    font-size: 20px;
    font-weight: 600;
    color: var(--cd-text);
  }

  &__form {
    background: var(--cd-card-bg);
    border-radius: 20px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-align: left;
  }

  &__formTitle {
    color: var(--cd-base-green);
    font-size: 16px;
    font-family: var(--cd-default-font);
  }

  &__formGrid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;

    @include respond-to('md') {
      grid-template-columns: 1fr;
    }
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    &--wide {
      grid-column: 1 / -1;
    }

    &--content {
      :deep(.formInput) {
        min-height: 120px;
        height: auto;
        resize: vertical;
        width: 100%;
      }
    }
  }

  &__label {
    color: var(--cd-gray-alt);
    font-size: 13px;
  }

  &__imagePreview {
    max-width: 160px;
    max-height: 160px;
    object-fit: contain;
    border-radius: 100px;
    cursor: zoom-in;
  }

  &__thumb {
    display: block;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    background: var(--cd-bg-dark);
    cursor: zoom-in;

    &--empty {
      background: transparent;
      border: 1px solid var(--cd-border);
      cursor: default;
    }
  }

  &__checks {
    display: flex;
    gap: 24px;
  }

  &__infoBlock {
    display: flex;
    gap: 16px;

    :deep(.formInput) {
      background: var(--cd-bg-dark);
      color: var(--cd-text-muted);
      cursor: default;
      border-color: transparent;
    }
  }

  &__filters {
    background: var(--cd-card-bg);
    border-radius: 20px;
    padding: 20px;
  }

  &__filterRow {
    display: flex;
    gap: 16px;
    align-items: flex-end;
    flex-wrap: wrap;

    @include respond-to('md') {
      flex-direction: column;
      align-items: stretch;
    }
  }

  &__filterField {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 180px;
  }

  &__filterActions {
    display: flex;
    gap: 8px;
    align-items: flex-end;
  }

  &__passwordBlock {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-top: 16px;
    border-top: 1px solid var(--cd-border);
  }

  &__formActions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
  }

  &__actions {
    vertical-align: middle;
    white-space: nowrap;
    font-size: 0;

    .adminCrud__actionButton + .adminCrud__actionButton {
      margin-left: 8px;
    }
  }

  &__footer {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    padding-top: 8px;
  }

  &__actionButton {
    background: transparent;
    border: 1px solid var(--cd-base-green);
    color: var(--cd-base-green);
    border-radius: 8px;
    padding: 6px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;

    &:hover {
      background: var(--cd-base-green);
      color: #ffffff;
    }

    &:disabled {
      opacity: 0.4;
      cursor: default;

      &:hover {
        background: transparent;
        color: var(--cd-base-green);
      }
    }

    &--danger {
      border-color: var(--cd-danger);
      color: var(--cd-danger);

      &:hover {
        background: var(--cd-danger);
        color: #ffffff;
      }

      &:disabled:hover {
        background: transparent;
        color: var(--cd-danger);
      }
    }
  }
}

.adminTable {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 8px;
  text-align: left;

  @include respond-to('md') {
    display: block;
    overflow-x: auto;
  }

  th {
    color: var(--cd-text-muted);
    font-size: 12px;
    font-weight: 400;
    padding: 0 16px 4px;
  }

  td {
    background: var(--cd-card-bg);
    padding: 12px 16px;
    font-size: 14px;
    color: var(--cd-text);

    &:first-child {
      border-radius: 12px 0 0 12px;
    }

    &:last-child {
      border-radius: 0 12px 12px 0;
    }
  }
}
</style>
